/**
 * Remix formats: what the AI writes around the clip. The ids are the API's
 * (backend/api/api/routes/remix.py); what each asks of the writer lives in
 * agent/agent/remix.py. The words a person reads are in the dictionaries
 * (i18n/messages, `clips.remix.*`), so the dialog speaks the site's language.
 */
import { makeT } from '../../i18n/translate';
import { sellsNextVideo } from '../../utils/renewal';

// `key` names the copy in clips.remix.formats; `example` says whether it has one
export const REMIX_PRESETS = [
  { id: 'did_you_know', key: 'didYouKnow', example: true },
  { id: 'explainer', key: 'explainer', example: true },
  { id: 'hot_take', key: 'hotTake', example: true },
  { id: 'backstory', key: 'backstory', example: true },
  { id: 'custom', key: 'custom', example: false },
];

export const DEFAULT_REMIX_PRESET = 'did_you_know';

// How often the AI stops the clip to comment (agent/agent/remix.py COMMENTARY).
// The words are clips.remix.levels.<id>.
export const COMMENTARY_LEVELS = [{ id: 'light' }, { id: 'balanced' }, { id: 'lots' }];
export const DEFAULT_COMMENTARY = 'balanced';

/**
 * Remix is off unless the server and the build say otherwise: the button shows
 * only where VITE_REMIX_ENABLED is 'true', and the API refuses it unless
 * REMIX_ENABLED is set (the switch to turn the feature off in production).
 */
export const remixEnabled = () => import.meta.env.VITE_REMIX_ENABLED === 'true';

const EN = makeT('en');

/** What went wrong, in words a person can act on. */
export function remixErrorMessage(error, t = EN) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 404 && detail === 'Not found') return t('clips.remix.off');
  // An own voice that was deleted or cannot be reached (api/services/voices.py)
  if (['voice_not_found', 'own_voice_too_long', 'voice_unavailable'].includes(detail)) return t(`ownVoice.errors.${detail}`);
  // The API's own words (a refused plan, a clip that has gone): it says them
  // in English, and saying nothing instead would be worse
  if (typeof detail === 'string' && detail) return detail;
  return t('clips.remix.failed');
}

/** No AI video left to make a remix with (/usage/metrics); unknown or unlimited is not out. */
export function outOfVideos(metrics) {
  const usage = metrics?.ai_agent_projects;
  if (!usage || usage.limit === 'Unlimited') return false;
  return usage.used >= usage.limit;
}

/**
 * The remix is sold, not refused: a free account with no video left meets the Remix offer
 * (RemixOffer.jsx); a subscriber who used the month's meets "add to my plan" (UpgradeOffer).
 */
export const remixNeedsPro = (metrics) => packRemixesLeft(metrics) === 0 && outOfVideos(metrics) && sellsNextVideo(metrics);

/** The remixes a Clip pack still holds (api/services/clip_packs.py), 0 when none or no pack. */
export const packRemixesLeft = (metrics) => Number(metrics?.clip_pack?.remixes_left) || 0;

/** The videos a remix could still come from, or null when there is no count to say (unlimited, unknown). */
export function videosLeft(metrics) {
  // A pack's remixes go first (routes/remix.py)
  if (packRemixesLeft(metrics) > 0) return packRemixesLeft(metrics);
  const usage = metrics?.ai_agent_projects;
  if (!usage || usage.limit === 'Unlimited' || typeof usage.limit !== 'number') return null;
  return Math.max(0, usage.limit - usage.used);
}
